import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import "../../../../css/ProcurementForm.css";
import { cleanHtml, computeSoumErrors, isSoumissionnaireComplet, isCriterionComplete, formatEuro, sanitizePond, getOrdinal } from "../../utils/procurementForm.utils";
import IdentificationDemandeur from "./IdentificationDemandeur";
import ProcurementMarketInfoSection from "./ProcurementMarketInfoSection";
import ProcurementAdditionalInfoSection from "./ProcurementAdditionalInfoSection";
import ProcurementAwardCriteriaSection from "./ProcurementAwardCriteriaSection";
import ProcurementBiddersSection from "./ProcurementBiddersSection";
import { toast } from "react-toastify";
import { getUsers } from "../../../../services/api/userService";
import { postProcurement, updateProcurement } from "../../../../services/api/procurementService";
import { str2bool } from "../../../../helpers/formatHelper";
import Button from "../../../../components/ui/Button";
import SpinnerIcon from "../../../../assets/icons/spinner";
import { useLoading } from "../../../../context/LoadingContext";
import StatusBadge from "../../../../components/ui/StatusBadge";
import { Step } from "../../../../helpers/stepHelper";

const INITIAL_CRITERIA = [
  {
    id: "prix",
    label: "Prix",
    pond: 0,
    description: `<p>Le pouvoir adjudicateur octroiera le maximum de points au soumissionnaire ayant proposé l'offre la plus basse, les cotes données aux autres soumissionnaires étant calculées à la proportionnelle, suivant la règle de trois&nbsp;:</p><p><br/></p><p><em>Score offre = (prix de l'offre la plus basse / prix de l'offre) x pondération du critère prix</em></p>`,
    fixed: true,
  },
];

const INITIAL_SOUMISSIONNAIRE = {
  civility: "",
  numeroBCE: "",
  nom: "",
  personneContact: "",
  job: "",
  emails: [""],
  telephone: "",
};

const INITIAL_SOUM_TOUCH = {
  civility: false,
  numeroBCE: false,
  nom: false,
  personneContact: false,
  job: false,
  emails: [false],
};

const INITIAL_CRITERIA_SELECTION = [{ detail: "" }];

const CRITERIA_MAX = 10;
const TVA_FACTOR = 1.21;
const SEUIL_HTVA = 30000;

export default function ProcurementForm({ onSave = null, data: initialData = null, setProcurement = null }) {
  const { setIsLoading } = useLoading();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [montantAffiche, setMontantAffiche] = useState("");
  const [criteria, setCriteria] = useState(
    initialData?.criteria?.length > 0
      ? initialData.criteria.map((criterion) => {
        return { id: criterion.uuid, label: criterion.name, pond: criterion.weightingRate, description: criterion.description, fixed: criterion.index === 0 };
      })
      : INITIAL_CRITERIA,
  );
  const [bidders, setSoumissionnaires] = useState(
    initialData?.enterprises?.length > 0
      ? initialData.enterprises.map((enterprise) => {
        return {
          numeroBCE: enterprise.enterpriseNumber,
          nom: enterprise.name,
          civility: enterprise.contacts[0].civility,
          personneContact: enterprise.contacts[0].fullname,
          job: enterprise.contacts[0].job,
          emails: enterprise.contacts.map(contact => contact.email),
          telephone: enterprise.contacts[0].mobile,
        };
      })
      : [INITIAL_SOUMISSIONNAIRE],
  );
  const [soumsTouched, setSoumsTouched] = useState(
    initialData?.enterprises?.length > 0
      ? initialData.enterprises.map((enterprise) => {
        return {
          numeroBCE: enterprise.enterpriseNumber,
          nom: enterprise.name,
          civility: enterprise.contacts[0].civility,
          personneContact: enterprise.contacts[0].fullname,
          job: enterprise.contacts[0].job,
          emails: enterprise.contacts.map(contact => contact.email),
          telephone: enterprise.contacts[0].mobile,
        };
      })
      : [INITIAL_SOUM_TOUCH],
  );
  const [optionsEmailDemandeur, setOptionsEmailDemandeur] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errorUsers, setErrorUsers] = useState(null);
  const [cleanedCriteriaDescriptions, setCleanedCriteriaDescriptions] = useState([]);
  const [cleanedServicesContents, setCleanedServicesContents] = useState([]);
  const [cleanedSelectionCriteriaDescriptions, setCleanedSelectionCriteriaDescriptions] = useState([]);
  const criteriaSelection = initialData?.criteriaSelection?.length > 0
    ? initialData.criteriaSelection.map((criterion) => {
      return { detail: criterion.description };
    })
    : INITIAL_CRITERIA_SELECTION;
  const [onSubmitting, setOnSubmitting] = useState(false);

  const {
    control,
    register,
    setValue,
    watch,
    unregister,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      context: initialData?.context ?? "",
      criteriaSelection: initialData?.criteriaSelection?.length > 0 ? "true" : "false",
      dailyRate: initialData?.dailyRate ?? null,
      duration: initialData?.duration ?? "",
      endAt: initialData?.endAt ? new Date(initialData.endAt) : null,
      firstTrancheStartAt: initialData?.firstTrancheStartAt ? new Date(initialData.firstTrancheStartAt) : null,
      firstTrancheEndAt: initialData?.firstTrancheEndAt ? new Date(initialData.firstTrancheEndAt) : null,
      fundingSource: initialData?.fundingSource ?? "",
      offerLimitAt: initialData?.offerLimitAt ? new Date(initialData.offerLimitAt) : null,
      uuidInitiator: initialData?.initiator?.uuid ?? "",
      hasQualitativeSelection: initialData?.hasQualitativeSelection?.toString() ?? "false",
      hasTranches: initialData?.hasTranches?.toString() ?? "false",
      amountVAT: initialData?.amountVAT ?? "",
      name: initialData?.name ?? "",
      object: initialData?.object ?? "",
      services: initialData?.services?.length > 0 ? initialData.services : [{ name: "", description: "" }],
      canSubcontracting: initialData?.canSubcontracting?.toString() ?? "false",
      TypeFacturation: initialData?.invoiceType ?? "",
      TypeFacturationAutre: initialData?.invoiceType ?? "",
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "services" });

  const modules = {
    toolbar: [
      [{ header: [false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };
  const editorStyle = { height: "200px", marginBottom: "20px" };

  const handleEmailChange = (soumIdx, emailIdx, value) => {
    setSoumissionnaires(soums =>
      soums.map((s, i) =>
        i === soumIdx
          ? { ...s, emails: s.emails.map((e, j) => (j === emailIdx ? value : e)) }
          : s,
      ),
    );
    setSoumsTouched(touches =>
      touches.map((t, i) =>
        i === soumIdx
          ? {
            ...t,
            emails: Array.isArray(t.emails)
              ? t.emails.map((v, j) => (j === emailIdx ? true : v))
              : [],
          }
          : t,
      ),
    );
  };

  const handleAddEmail = (soumIdx) => {
    setSoumissionnaires(soums =>
      soums.map((s, i) =>
        i === soumIdx ? { ...s, emails: [...s.emails, ""] } : s,
      ),
    );
    setSoumsTouched(touches =>
      touches.map((t, i) =>
        i === soumIdx
          ? { ...t, emails: Array.isArray(t.emails) ? [...t.emails, false] : [false] }
          : t,
      ),
    );
  };

  const handleRemoveEmail = (soumIdx, emailIdx) => {
    setSoumissionnaires(soums =>
      soums.map((s, i) =>
        i === soumIdx
          ? { ...s, emails: s.emails.filter((_, j) => j !== emailIdx) }
          : s,
      ),
    );
    setSoumsTouched(touches =>
      touches.map((t, i) =>
        i === soumIdx
          ? { ...t, emails: Array.isArray(t.emails) ? t.emails.filter((_, j) => j !== emailIdx) : [] }
          : t,
      ),
    );
  };

  const soumsErrors = bidders.map(computeSoumErrors);

  const handleSoumFieldChange = (idx, field, value) => {
    setSoumissionnaires(soums =>
      soums.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
    );
    setSoumsTouched(touches =>
      touches.map((t, i) => (i === idx ? { ...t, [field]: true } : t)),
    );
  };

  const handleAddSoumissionnaire = () => {
    const last = bidders[bidders.length - 1];
    const err = computeSoumErrors(last);
    const hasErrors = Object.values(err).some(e => Array.isArray(e) ? e.some(Boolean) : e);

    if (hasErrors) {
      setSoumsTouched(prev =>
        prev.map((t, idx) =>
          idx === bidders.length - 1
            ? {
              civility: true,
              numeroBCE: true,
              nom: true,
              personneContact: true,
              job: true,
              emails: last.emails.map(() => true),
            }
            : t,
        ),
      );
      return;
    }
    setSoumissionnaires(prev => [...prev, { ...INITIAL_SOUMISSIONNAIRE }]);
    setSoumsTouched(prev => [...prev, { ...INITIAL_SOUM_TOUCH }]);
  };

  const handleRemoveSoumissionnaire = (idx) => {
    setSoumissionnaires(arr => arr.filter((_, i) => i !== idx));
    setSoumsTouched(arr => arr.filter((_, i) => i !== idx));
  };

  const values = watch();
  const services = useMemo(() => values.services || [], [values.services]);
  const typeFacturation = values.TypeFacturation;
  const amountVAT = values.amountVAT;
  const montantHTVA = amountVAT ? Number(amountVAT) / TVA_FACTOR : 0;
  const showInformationsComplementaires = montantHTVA > SEUIL_HTVA;

  useEffect(() => {
    setMontantAffiche(amountVAT ? formatEuro(String(amountVAT)) : "");
  }, [amountVAT]);

  useEffect(() => {
    if (values.criteriaSelection === "") {
      setValue("criteriaSelection", null, { shouldValidate: false, shouldDirty: false });
    }
  }, [setValue, values.criteriaSelection]);

  useEffect(() => {
    register("fundingSource", { required: "La source de financement est obligatoire" });
  }, [register]);

  useEffect(() => {
    if (typeFacturation !== "Autre") setValue("TypeFacturationAutre", "");
  }, [typeFacturation, setValue]);

  useEffect(() => {
    if (montantHTVA > SEUIL_HTVA) {
      register("context", {
        required: "Le contexte est obligatoire si le montant dépasse 30 000 € HTVA",
      });
    } else {
      unregister("context", { keepValue: true });
    }
    register("object", { required: "L'objet est obligatoire" });
  }, [register, unregister, montantHTVA]);

  useEffect(() => {
    let cancelled = false;
    async function fetchUsers() {
      setLoadingUsers(true);
      try {
        const users = await getUsers();
        if (!cancelled) {
          const mapped = (users || []).map((u) => {
            const parts = (u.fullname || "").trim().split(" ");
            const prenom = parts[0] || "";
            const nom = parts.slice(1).join(" ");
            return {
              uuid: u.uuid,
              prenom,
              nom,
              email: u.email,
              manager: u.manager,
            };
          });
          mapped.sort((a, b) => {
            const cmpPrenom = a.prenom.localeCompare(b.prenom, 'fr', { sensitivity: 'base' });
            if (cmpPrenom !== 0) return cmpPrenom;
            return a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' });
          });
          setOptionsEmailDemandeur(mapped);
          setErrorUsers(null);
        }
      } catch {
        if (!cancelled) setErrorUsers("Erreur lors du chargement des utilisateurs.");
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    }
    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (showInformationsComplementaires) {
      unregister("offerLimitAt");
    } else {
      register("offerLimitAt", {
        required: "La date et l'heure limite de remise des offres sont obligatoires",
        validate: value => !!value || "La date et l'heure limite de remise des offres sont obligatoires",
      });
    }
  }, [showInformationsComplementaires, amountVAT, register, unregister]);

  const filteredOptions = useMemo(() => {
    const searchTerm = (values.EmailDemandeurLabel || "").toLowerCase();
    return optionsEmailDemandeur.filter(o =>
    (o.prenom?.toLowerCase().includes(searchTerm)
      || o.nom?.toLowerCase().includes(searchTerm)
      || o.email?.toLowerCase().includes(searchTerm)),
    );
  }, [optionsEmailDemandeur, values.EmailDemandeurLabel]);

  const totalPond = useMemo(() =>
    criteria.reduce((acc, c) => {
      const val = parseFloat((c.pond || "").toString().replace(",", "."));
      return acc + (isNaN(val) ? 0 : val);
    }, 0), [criteria]);

  const isCriteriaValid = useMemo(() =>
    criteria.length > 0
    && totalPond === 100
    && criteria.every(isCriterionComplete), [criteria, totalPond]);

  const lastService = services[services.length - 1];
  const canAddService = !!(
    lastService
    && lastService.name
    && lastService.name.trim() !== ""
    && lastService.description
    && lastService.description.replace(/<(.|\n)*?>/g, "").trim() !== ""
  );

  const canAddSoumissionnaire = isSoumissionnaireComplet(
    bidders[bidders.length - 1],
  );

  const isFormComplet = useCallback(() => {
    const montant = Number(values.amountVAT);
    const montantHT = montant ? montant / TVA_FACTOR : 0;
    const showInfos = montantHT > SEUIL_HTVA;

    let requiredFields = [
      values.endAt,
      values.duration,
      values.uuidInitiator,
      values.amountVAT,
      values.name,
      bidders.length > 0,
      values.object,
      values.fundingSource,
    ];

    if (montantHT > SEUIL_HTVA) {
      requiredFields.push(values.context);
    }
    if (typeof values.hasQualitativeSelection !== "undefined" && values.hasQualitativeSelection !== null && values.hasQualitativeSelection !== "") {
      requiredFields.push(values.hasQualitativeSelection);
    }
    if (typeof values.criteriaSelection !== "undefined" && values.criteriaSelection !== null && values.criteriaSelection !== "") {
      requiredFields.push(values.criteriaSelection);
    }
    if (!showInfos) {
      requiredFields.push(values.offerLimitAt);
    }
    if (showInfos) {
      requiredFields.push(
        values.hasTranches,
        values.canSubcontracting,
        values.TypeFacturation,
      );
      if (str2bool(values.hasTranches)) {
        requiredFields.push(
          values.firstTrancheStartAt,
          values.firstTrancheEndAt,
        );
      }
      if (values.TypeFacturation === "Autre") {
        requiredFields.push(values.TypeFacturationAutre);
      }
      if (
        !services
        || !Array.isArray(services)
        || services.length === 0
        || !services.every(
          s =>
            s.name
            && s.name.trim() !== ""
            && s.description
            && s.description.replace(/<(.|\n)*?>/g, "").trim() !== "",
        )
      ) {
        return false;
      }
    }
    return requiredFields.every(
      v =>
        typeof v === "boolean"
        || (v !== undefined && v !== null && `${v}`.trim() !== ""),
    );
  }, [values, bidders, services]);

  const removeCriteria = id => setCriteria(prev => prev.filter(c => c.id !== id));
  const onChangeField = (id, field, value) =>
    setCriteria(prev => prev.map(c => (c.id === id ? { ...c, [field]: value } : c)));
  const onChangeDescription = (id, html) =>
    setCriteria(prev => prev.map(c => (c.id === id ? { ...c, description: html } : c)));
  const addCriteria = () => {
    if (criteria.length >= CRITERIA_MAX) return;
    setCriteria(prev => [
      ...prev,
      {
        id: `crit${prev.length + 1}`,
        label: "",
        pond: "",
        description: "<p></p>",
        fixed: false,
      },
    ]);
  };

  const handleSelect = (option) => {
    setValue("uuidInitiator", option.uuid, { shouldValidate: true, shouldDirty: true });
    setValue("EmailDemandeurLabel", `${option.prenom} ${option.nom} (${option.email})`, { shouldValidate: false, shouldDirty: true });
    setHasSelected(true);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!hasSelected) {
        setValue("uuidInitiator", "", { shouldValidate: true, shouldDirty: true });
        setValue("EmailDemandeurLabel", "", { shouldValidate: true, shouldDirty: true });
      }
      setHasSelected(false);
      setShowSuggestions(false);
    }, 100);
  };

  function removeDefaultContext(html) {
    if (!html) return null;

    const defaultBlock = `à la transformation numérique de la Wallonie en s'appuyant sur la stratégie numérique Digital Wallonia.`;
    const regex = new RegExp(defaultBlock + '(\\n)?');
    const match = html.match(regex);

    if (!match) {
      return html.trim() || null;
    }

    const endIndex = match.index + match[0].length;

    const afterBlock = html.slice(endIndex).trim();

    return afterBlock || null;
  }

  const isFormMinimal = () =>
    values.uuidInitiator
    && values.uuidInitiator.trim() !== ""
    && values.name
    && values.name.trim() !== "";

  function prepareFormData({
    values,
    criteria,
    bidders,
    cleanedCriteriaDescriptions,
    cleanedServicesContents,
    optionsEmailDemandeur,
    mode,
    montantHTVA,
    SEUIL_HTVA,
  }) {
    let result = transformDataForBackend({
      ...values,
      object: cleanHtml(values.object) || null,
      context: removeDefaultContext(cleanHtml(values.context)) || "<span></span>",
      duration: values.duration || null,
      fundingSource: values.fundingSource || null,
      criteria: criteria.map((c, i) => ({
        ...c,
        description: cleanedCriteriaDescriptions[i] || c.description,
      })),
      bidders: bidders,
      services: (values.services || []).map((s, i) => ({
        ...s,
        description: cleanedServicesContents[i] || "",
      })),
      mode: mode,
    }, optionsEmailDemandeur);

    if (montantHTVA > SEUIL_HTVA && "offerLimitAt" in result) {
      delete result.offerLimitAt;
    }
    if ("dailyRate" in result) {
      result.dailyRate = result.dailyRate ? Number(result.dailyRate) : null;
    }

    const sortedEntries = Object.entries(result).sort(([a], [b]) => a.localeCompare(b));
    return Object.fromEntries(sortedEntries);
  }

  // ON ENREGISTRE LE BROUILLON
  async function onSaveDraft(e) {
    try {
      e.preventDefault();
      if (!isFormMinimal()) {
        return;
      }
      setIsLoading(true);
      setOnSubmitting(true);
      const dataMapped = prepareFormData({
        values,
        criteria,
        bidders,
        cleanedCriteriaDescriptions,
        cleanedServicesContents,
        cleanedSelectionCriteriaDescriptions,
        optionsEmailDemandeur,
        mode: "draft",
        montantHTVA,
        SEUIL_HTVA,
      });

      let result;
      if (initialData) {
        let data = dataMapped;
        // EDGE CASE FOR CFO EDITOR
        if (initialData?.step?.name === Step.BUDGET_LEGAL_CHECK) {
          data = {
            ...dataMapped,
            documentsOptions: [
              {
                type: "SPECIFICATIONS",
                format: "word",
              },
              {
                type: "LAUNCH_NOTE",
                format: "word",
              },
            ],
          };
        }
        result = await updateProcurement(initialData.uuid, data);
      } else {
        result = await postProcurement(dataMapped);
      }
      if (setProcurement) await setProcurement(result);
      if (onSave) await onSave();
      toast.success("Les informations ont été mises à jour avec succès !");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
      setOnSubmitting(false);
    }
  }

  const canSubmit = useMemo(() =>
    isFormComplet()
    && isCriteriaValid
    && bidders.length >= 3
    && bidders.every(isSoumissionnaireComplet),
    [isFormComplet, isCriteriaValid, bidders],
  );

  // Calcul détaillé des champs manquants pour le submit
  const missingFields = useMemo(() => {
    const missing = [];
    const isEmpty = v => v === undefined || v === null || `${v}`.trim?.() === "" || (typeof v === "string" && v.length === 0);

    // Champs généraux
    if (isEmpty(values.uuidInitiator)) missing.push("Email du demandeur");
    if (isEmpty(values.name)) missing.push("Nom du marché");
    if (isEmpty(values.object)) missing.push("Objet");
    if (isEmpty(values.fundingSource)) missing.push("Source de financement");
    if (isEmpty(values.amountVAT)) missing.push("Montant TVAC");
    if (isEmpty(values.duration)) missing.push("Durée");
    if (isEmpty(values.endAt)) missing.push("Date de fin du marché");

    if (values.hasQualitativeSelection === "" || values.hasQualitativeSelection == null) {
      missing.push("Sélection qualitative (oui/non)");
    }
    if (values.criteriaSelection === "" || values.criteriaSelection == null) {
      missing.push("Critères de sélection (oui/non)");
    }

    // Montant/infos complémentaires
    if (montantHTVA > SEUIL_HTVA) {
      if (isEmpty(values.context)) missing.push("Contexte (obligatoire > 30 000 € HTVA)");
      if (values.hasTranches === "" || values.hasTranches == null) {
        missing.push("Marché en tranches (oui/non)");
      } else if (str2bool(values.hasTranches)) {
        if (isEmpty(values.firstTrancheStartAt)) missing.push("Début 1ère tranche");
        if (isEmpty(values.firstTrancheEndAt)) missing.push("Fin 1ère tranche");
      }
      if (values.canSubcontracting === "" || values.canSubcontracting == null) {
        missing.push("Sous-traitance autorisée (oui/non)");
      }
      if (isEmpty(values.TypeFacturation)) {
        missing.push("Type de facturation");
      } else if (values.TypeFacturation === "Autre" && isEmpty(values.TypeFacturationAutre)) {
        missing.push("Type de facturation (Autre - préciser)");
      }
      // Services
      if (!services || !Array.isArray(services) || services.length === 0) {
        missing.push("Services (au moins un service avec nom + description)");
      } else {
        const invalidService = services.some(
          s =>
            !s?.name
            || s.name.trim() === ""
            || !s?.description
            || s.description.replace(/<(.|\n)*?>/g, "").trim() === "",
        );
        if (invalidService) {
          missing.push("Services: chaque service doit avoir un nom et une description");
        }
      }
    } else {
      if (isEmpty(values.offerLimitAt)) missing.push("Date limite de remise des offres");
    }

    // Critères d'attribution
    if (criteria.length === 0) {
      missing.push("Critères d'attribution (au moins un critère)");
    } else {
      if (totalPond !== 100) {
        missing.push("Critères d'attribution: la pondération totale doit être égale à 100");
      }
      if (!criteria.every(isCriterionComplete)) {
        missing.push("Critères d'attribution: chaque critère doit avoir un libellé, une pondération et une description");
      }
    }

    // Soumissionnaires
    if (bidders.length < 3) {
      missing.push("Soumissionnaires: au moins 3 requis");
    }
    const incompleteIdx = bidders
      .map((s, i) => ({ i, ok: isSoumissionnaireComplet(s) }))
      .filter(x => !x.ok)
      .map(x => x.i);
    if (incompleteIdx.length > 0) {
      incompleteIdx.forEach((i) => {
        const err = computeSoumErrors(bidders[i]);
        const parts = [];
        if (err.civility) parts.push("civilité");
        if (err.numeroBCE) parts.push("numéro BCE");
        if (err.nom) parts.push("nom entreprise");
        if (err.personneContact) parts.push("personne de contact");
        if (err.job) parts.push("fonction");
        if (Array.isArray(err.emails) && err.emails.some(Boolean)) parts.push("email(s)");
        missing.push(`Soumissionnaire ${i + 1}: ${parts.join(", ")}`);
      });
    }

    return missing;
  }, [
    values,
    services,
    criteria,
    totalPond,
    bidders,
    montantHTVA,
  ]);

  function transformDataForBackend(raw) {
    let result = { ...raw };
    if ("context" in result) {
      if (!result.context || result.context.trim() === "") {
        result.context = "<span></span>";
      }
    }

    if ("uuidInitiator" in result) {
      const selectedUser = optionsEmailDemandeur.find(
        u => u.uuid === result.uuidInitiator,
      );
      result.uuidManager = selectedUser?.manager?.uuid || null;
      delete result.EmailDemandeurLabel;
    }
    if ("amountVAT" in result) {
      result.amountVAT = result.amountVAT ? Number(result.amountVAT) : null;
      result.amountExcludingVAT = result.amountVAT ? Number((result.amountVAT / TVA_FACTOR).toFixed(2)) : null;
    }
    if ("hasTranches" in result) {
      result.hasTranches = str2bool(result.hasTranches);
    }
    if ("firstTrancheStartAt" in result) {
      if (result.firstTrancheStartAt === "" || result.firstTrancheStartAt == null) {
        delete result.firstTrancheStartAt;
      }
    }
    if ("firstTrancheEndAt" in result) {
      if (result.firstTrancheEndAt === "" || result.firstTrancheEndAt == null) {
        delete result.firstTrancheEndAt;
      }
    }
    if ("canSubcontracting" in result) {
      result.canSubcontracting = str2bool(result.canSubcontracting);
    }
    if ("TypeFacturationAutre" in result) {
      if (result.TypeFacturationAutre && result.TypeFacturationAutre.trim() !== "") {
        result.invoiceType = result.TypeFacturationAutre || null;
      } else if ("TypeFacturation" in result) {
        result.invoiceType = result.TypeFacturation || null;
      }
      delete result.TypeFacturationAutre;
      if ("TypeFacturation" in result) delete result.TypeFacturation;
    } else if ("TypeFacturation" in result) {
      result.invoiceType = result.TypeFacturation || null;
      delete result.TypeFacturation;
    }
    if ("services" in result) {
      result.services = result.services.map(s => ({
        name: s.name || null,
        description: s.description || null,
      }));
      if (result.services.length === 0) {
        delete result.services;
      }
    }
    if ("criteriaSelection" in result) {
      result.hasCriteriaSelection = str2bool(result.criteriaSelection);
    }

    if (result.hasCriteriaSelection) {
      result.criteriaSelection = cleanedSelectionCriteriaDescriptions.filter(
        c => c && c.replace(/<(.|\n)*?>/g, "").trim() !== "",
      ) || null;
    } else {
      delete result.criteriaSelection;
    }
    if ("hasQualitativeSelection" in result) {
      result.hasQualitativeSelection = str2bool(result.hasQualitativeSelection);
    }
    if ("criteria" in result) {
      result.criteria = result.criteria.map(c => ({
        name: c.label,
        description: c.description,
        weightingRate: Number(c.pond),
      }));
    }
    if ("bidders" in result) {
      result.bidders = result.bidders.map(s => ({
        name: s.nom || null,
        enterpriseNumber: s.numeroBCE || null,
        contacts: [
          {
            civility: s.civility || null,
            fullname: s.personneContact || null,
            job: s.job || null,
            emails: Array.isArray(s.emails)
              ? s.emails.filter(email => !!email && email.trim() !== "")
              : [],
            mobile: s.telephone || null,
          },
        ],
      }));
    }
    if (montantHTVA > SEUIL_HTVA && "offerLimitAt" in result) {
      delete result.offerLimitAt;
    }

    return result;
  }
  return (
    <form className="max-w-5xl mx-auto p-8 flex flex-col gap-8 procurement-form">
      {/* Identification du demandeur */}
      {!initialData && (
        <IdentificationDemandeur
          values={values}
          setValue={setValue}
          errors={errors}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          hasSelected={hasSelected}
          setHasSelected={setHasSelected}
          filteredOptions={filteredOptions}
          handleSelect={handleSelect}
          handleBlur={handleBlur}
          loadingUsers={loadingUsers}
          errorUsers={errorUsers}
        />
      )}
      {/* Renseignements marché */}
      <ProcurementMarketInfoSection
        montantAffiche={montantAffiche}
        setValue={setValue}
        errors={errors}
        watch={watch}
        values={values}
      />
      {/* Informations complémentaires */}
      <ProcurementAdditionalInfoSection
        showInformationsComplementaires={showInformationsComplementaires}
        typeFacturation={typeFacturation}
        setValue={setValue}
        watch={watch}
        values={values}
        errors={errors}
        register={register}
        fields={fields}
        append={append}
        remove={remove}
        services={services}
        criteriaSelection={criteriaSelection}
        canAddService={canAddService}
        modules={modules}
        editorStyle={editorStyle}
        onCleanedServicesChange={setCleanedServicesContents}
        onCleanedCriteriaChange={setCleanedSelectionCriteriaDescriptions}
      />
      {/* Critères d'attribution */}
      <ProcurementAwardCriteriaSection
        criteria={criteria}
        totalPond={totalPond}
        getOrdinal={getOrdinal}
        sanitizePond={sanitizePond}
        onChangeField={onChangeField}
        onChangeDescription={onChangeDescription}
        removeCriteria={removeCriteria}
        addCriteria={addCriteria}
        isCriterionComplete={isCriterionComplete}
        onCleanedCriteriaChange={setCleanedCriteriaDescriptions}
      />
      {/* Soumissionnaires */}
      <ProcurementBiddersSection
        bidders={bidders}
        soumsErrors={soumsErrors}
        soumsTouched={soumsTouched}
        handleRemoveSoumissionnaire={handleRemoveSoumissionnaire}
        handleSoumFieldChange={handleSoumFieldChange}
        setSoumissionnaires={setSoumissionnaires}
        setSoumsTouched={setSoumsTouched}
        handleAddSoumissionnaire={handleAddSoumissionnaire}
        canAddSoumissionnaire={canAddSoumissionnaire}
        handleEmailChange={handleEmailChange}
        handleAddEmail={handleAddEmail}
        handleRemoveEmail={handleRemoveEmail}
      />
      <div className="flex flex-row justify-between items-start mt-6 mb-16">
        <div className="flex flex-col items-start">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="medium"
              color="blue"
              onClick={onSaveDraft}
              disabled={!isFormMinimal() || onSubmitting}
              title="Enregistrer le brouillon du marché (au moins email et nom requis)"
            >
              {initialData ? "Enregistrer" : "Créer"}
            </Button>
            {onSubmitting && (
              <div className="w-10">
                <SpinnerIcon />
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Vous pouvez {initialData ? "enregistrer" : "créer"} le formulaire à tout moment
          </div>
        </div>
        {/* Liste des champs manquants pour validation */}
        <div className="flex flex-col items-end">
          <div className={!canSubmit ? "cursor-not-allowed" : ""}>
            <StatusBadge
              color={canSubmit ? "green" : "alert"}
              text={canSubmit ? "Complet" : "Incomplet"}
              size="large"
            />
          </div>

          {!canSubmit && missingFields.length > 0 && (
            <div
              style={{ backgroundColor: "#fffafa" }}
              className="mt-3 border border-red-200 rounded-lg p-4 w-full"
            >
              <h4 className="text-sm font-semibold text-red-800 text-right mb-2">
                La validation exige tous les champs :
              </h4>
              <ul className="text-sm text-red-700 flex flex-col gap-1 items-end no-procurement-ul-style">
                {missingFields.map((m, idx) => (
                  <li
                    key={idx}
                    className="flex flex-row-reverse items-center gap-2 pointer-events-none cursor-default"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
